import { useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { FileWarning, Library, Pencil, Plus, ShieldCheck, SquarePen } from 'lucide-react';
import { api } from '@/shared/api/cliente';
import { Cargando } from '@/shared/ui/Cargando';
import { ErrorDeApi } from '@/shared/api/problema';
import type { MotivoDeCorreccion } from '@/shared/api/tipos';
import { EncabezadoDePagina } from '@/shared/ui/EncabezadoDePagina';
import { Interruptor } from '@/shared/ui/Interruptor';
import { PestanasDeCatalogos } from '@/modules/catalogos/PestanasDeCatalogos';
import { POR_PAGINA, Paginador } from '@/shared/ui/Paginador';
import { CampoDelPanel, PanelLateral, claseEntradaPanel } from '@/shared/ui/PanelLateral';
import { notificar } from '@/shared/ui/avisos';

export function PaginaMotivosDeCorreccion() {
  const clienteDeConsultas = useQueryClient();

  const [editando, setEditando] = useState<MotivoDeCorreccion | null>(null);
  const [creando, setCreando] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [pagina, setPagina] = useState(0);

  const motivos = useQuery({
    queryKey: ['motivos-de-correccion'],
    queryFn: () => api.get<MotivoDeCorreccion[]>('/motivos-de-correccion/catalogo'),
  });

  function refrescar() {
    setError(null);
    void clienteDeConsultas.invalidateQueries({ queryKey: ['motivos-de-correccion'] });
  }

  const cambiarEstado = useMutation({
    mutationFn: (m: MotivoDeCorreccion) =>
      api.put<MotivoDeCorreccion>(`/motivos-de-correccion/${m.id}`, {
        nombre: m.nombre,
        exigeObservacion: m.exigeObservacion,
        requiereAprobacion: m.requiereAprobacion,
        activo: !m.activo,
        orden: m.orden,
      }),
    onSuccess: refrescar,
    onError: (causa) => {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo cambiar el estado';
      setError(mensaje);
      notificar.error(mensaje);
    },
  });

  const catalogo = motivos.data ?? [];
  const tramo = catalogo.slice(pagina * POR_PAGINA, (pagina + 1) * POR_PAGINA);

  return (
    <>
      <EncabezadoDePagina
        titulo="Catálogos"
        subtitulo="Los vocabularios que alimenta EPAGAL"
        icono={Library}
        acciones={
          <button
            type="button"
            onClick={() => setCreando(true)}
            title="Nuevo motivo"
            aria-label="Nuevo motivo"
            className="grid size-10 place-items-center rounded-lg bg-marca-600 text-white transition-colors hover:bg-marca-700"
          >
            <Plus className="size-5" aria-hidden />
          </button>
        }
      />

      <PestanasDeCatalogos />

      {error && (
        <p
          role="alert"
          className="mb-4 rounded-lg border border-red-200 bg-red-50 px-4 py-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <div className="mb-5 flex gap-3 rounded-xl border border-blue-200 bg-blue-50 p-4">
        <FileWarning className="mt-0.5 size-4 shrink-0 text-blue-600" aria-hidden />
        <p className="text-justify text-xs uppercase leading-relaxed text-blue-800">
          <span className="font-semibold">El motivo dice por qué, no qué hacer.</span> El
          procedimiento —anular por dentro, tramitar ante el SRI o emitir una nota de crédito— lo
          determinan la situación del comprobante y si hay pago, que es normativa. El código lo
          asigna el sistema y no se reescribe: hay anulaciones apuntando a él. Un motivo que deje de
          usarse se <span className="font-semibold">retira</span>, nunca se borra.
        </p>
      </div>

      {motivos.isPending ? (
        <Cargando titulo="Cargando el catálogo" detalle="Trayendo los motivos de corrección" />
      ) : (
        <div className="overflow-hidden rounded-2xl border border-armazon-200 bg-white shadow-sm">
          <div className="hidden items-center gap-4 border-b border-armazon-100 bg-armazon-50/60 px-6 py-2.5 text-[10px] font-semibold uppercase tracking-wider text-armazon-400 lg:flex">
            <span className="w-20 shrink-0">Código</span>
            <span className="min-w-0 flex-[2] basis-72">Motivo</span>
            <span className="w-64 shrink-0">Al anular pedirá</span>
            <span className="w-24 shrink-0 text-center">Estado</span>
            <span className="w-10 shrink-0" aria-hidden />
          </div>

          <div className="divide-y divide-armazon-100">
            {tramo.map((m) => (
              <div
                key={m.id}
                className="flex flex-wrap items-center gap-4 px-6 py-4 transition-colors hover:bg-armazon-50/60"
              >
                <p className="importe w-20 shrink-0 text-xs font-semibold text-armazon-700">
                  {m.codigo}
                </p>

                <p
                  className={`min-w-0 flex-[2] basis-72 truncate text-sm font-medium uppercase ${
                    m.activo ? 'text-armazon-800' : 'text-armazon-400 line-through'
                  }`}
                >
                  {m.nombre}
                </p>

                <div className="flex w-64 shrink-0 flex-wrap gap-1.5">
                  {m.exigeObservacion && (
                    <Exigencia
                      icono={SquarePen}
                      texto="Explicar el caso"
                      clase="text-armazon-600"
                    />
                  )}
                  {m.requiereAprobacion && (
                    <Exigencia icono={ShieldCheck} texto="Visto bueno" clase="text-amber-700" />
                  )}
                  {!m.exigeObservacion && !m.requiereAprobacion && (
                    <span className="text-[10px] uppercase text-armazon-300">Solo el motivo</span>
                  )}
                </div>

                <div className="flex w-24 shrink-0 justify-center">
                  <Interruptor
                    activo={m.activo}
                    alCambiar={() => cambiarEstado.mutate(m)}
                    etiqueta={`Activar o retirar ${m.codigo}`}
                    deshabilitado={cambiarEstado.isPending}
                  />
                </div>

                <div className="flex w-10 shrink-0 justify-end">
                  <button
                    type="button"
                    onClick={() => setEditando(m)}
                    title="Editar"
                    aria-label={`Editar ${m.codigo}`}
                    className="rounded-lg p-1.5 text-armazon-400 transition-colors hover:bg-blue-50 hover:text-blue-600"
                  >
                    <Pencil className="size-[15px]" aria-hidden />
                  </button>
                </div>
              </div>
            ))}

            {catalogo.length === 0 && (
              <p className="px-6 py-12 text-center text-sm uppercase text-armazon-400">
                El catálogo está vacío
              </p>
            )}
          </div>

          <Paginador
            pagina={pagina}
            porPagina={POR_PAGINA}
            total={catalogo.length}
            quePagina="motivos"
            alCambiar={setPagina}
          />
        </div>
      )}

      <DialogoDeMotivo
        key={editando?.id ?? 'nuevo'}
        abierto={creando || editando !== null}
        motivo={editando}
        alCerrar={() => {
          setCreando(false);
          setEditando(null);
        }}
        alGuardar={refrescar}
      />
    </>
  );
}

function Exigencia({
  icono: Icono,
  texto,
  clase,
}: {
  icono: typeof SquarePen;
  texto: string;
  clase: string;
}) {
  return (
    <span
      className={`inline-flex items-center gap-1 rounded-md bg-armazon-100 px-1.5 py-0.5 text-[10px] font-semibold uppercase ${clase}`}
    >
      <Icono className="size-2.5 shrink-0" aria-hidden />
      {texto}
    </span>
  );
}

function DialogoDeMotivo({
  abierto,
  motivo,
  alCerrar,
  alGuardar,
}: {
  abierto: boolean;
  motivo: MotivoDeCorreccion | null;
  alCerrar: () => void;
  alGuardar: () => void;
}) {
  const editando = motivo !== null;

  const [nombre, setNombre] = useState(motivo?.nombre ?? '');
  const [exigeObservacion, setExigeObservacion] = useState(motivo?.exigeObservacion ?? true);
  const [requiereAprobacion, setRequiereAprobacion] = useState(motivo?.requiereAprobacion ?? false);
  const [orden, setOrden] = useState(motivo === null ? '0' : String(motivo.orden));
  const [error, setError] = useState<string | null>(null);
  const [enviando, setEnviando] = useState(false);

  const listo = nombre.trim().length >= 5;

  async function enviar(evento: React.FormEvent) {
    evento.preventDefault();
    if (!listo) {
      return;
    }

    setError(null);
    setEnviando(true);
    try {
      const cuerpo = {
        nombre: nombre.trim(),
        exigeObservacion,
        requiereAprobacion,
        activo: motivo?.activo ?? true,
        orden: Number(orden) || 0,
      };

      const guardado = editando
        ? await api.put<MotivoDeCorreccion>(`/motivos-de-correccion/${motivo.id}`, cuerpo)
        : await api.post<MotivoDeCorreccion>('/motivos-de-correccion', cuerpo);

      alGuardar();
      alCerrar();
      notificar.exito(
        `Motivo ${editando ? 'actualizado' : 'creado'}: ${guardado.codigo} ${guardado.nombre}`,
      );
    } catch (causa) {
      const mensaje = causa instanceof ErrorDeApi ? causa.message : 'No se pudo guardar el motivo';
      setError(mensaje);
      notificar.error(mensaje);
    } finally {
      setEnviando(false);
    }
  }

  return (
    <PanelLateral
      abierto={abierto}
      alCerrar={alCerrar}
      titulo={editando ? 'Editar motivo' : 'Nuevo motivo de corrección'}
      descripcion={
        editando ? `${motivo.codigo} · el código no cambia` : 'El código lo asigna el sistema'
      }
      icono={FileWarning}
      alEnviar={enviar}
      enviando={enviando || !listo}
    >
      {error && (
        <p
          role="alert"
          className="rounded-lg border border-red-200 bg-red-50 p-3 text-sm text-red-900"
        >
          {error}
        </p>
      )}

      <CampoDelPanel
        etiqueta="Motivo"
        requerido
        ayuda="Se guarda en mayúscula. Describa el tipo de error, no el caso concreto: el detalle se anota al anular cada factura."
      >
        <input
          value={nombre}
          onChange={(evento) => setNombre(evento.target.value)}
          maxLength={200}
          placeholder="Dirección de servicio o fiscal incorrecta"
          className={`${claseEntradaPanel} uppercase`}
        />
      </CampoDelPanel>

      <CampoDelPanel
        etiqueta="Qué pedirá al anular una factura"
        ayuda="Aquí vive la política de EPAGAL: se cambia sin tocar el sistema."
      >
        <div className="space-y-2">
          <Casilla
            marcada={exigeObservacion}
            alCambiar={setExigeObservacion}
            titulo="Explicar el caso"
            explicacion="«Dirección incorrecta» no sirve si no consta cuál era y cuál es."
          />
          <Casilla
            marcada={requiereAprobacion}
            alCambiar={setRequiereAprobacion}
            titulo="Visto bueno de un responsable"
            explicacion="Quién autoriza queda en su propio campo, no dentro del texto."
          />
        </div>
      </CampoDelPanel>

      <CampoDelPanel etiqueta="Orden en la lista" ayuda="Los más usados, primero.">
        <input
          type="number"
          min={0}
          value={orden}
          onChange={(evento) => setOrden(evento.target.value)}
          className={claseEntradaPanel}
        />
      </CampoDelPanel>
    </PanelLateral>
  );
}

function Casilla({
  marcada,
  alCambiar,
  titulo,
  explicacion,
}: {
  marcada: boolean;
  alCambiar: (valor: boolean) => void;
  titulo: string;
  explicacion: string;
}) {
  return (
    <label className="flex cursor-pointer gap-2.5 rounded-lg border border-armazon-200 p-3 transition-colors hover:border-marca-300">
      <input
        type="checkbox"
        checked={marcada}
        onChange={(evento) => alCambiar(evento.target.checked)}
        className="mt-0.5 size-4 shrink-0 accent-marca-600"
      />
      <span className="min-w-0">
        <span className="block text-xs font-semibold uppercase text-armazon-700">{titulo}</span>
        <span className="block text-[11px] leading-relaxed text-armazon-500">{explicacion}</span>
      </span>
    </label>
  );
}
